import type { Ref } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { formatDateRange } from "@/core/utils/date";
import type { IcmPeriod } from "@/icm/types";
import { PayPeriodLockedMark } from "@/payroll/pay-periods/pay-period-locked";
import { PAYROLL_RUN_STATUS_UI } from "@/payroll/pay-periods/payroll-run-status-ui";

interface IcmPeriodListProps {
  periods: IcmPeriod[];
  selectedIdx: number;
  selectedItemRef: Ref<HTMLButtonElement>;
  onSelect: (period: IcmPeriod) => void;
  className?: string;
}

export const IcmPeriodList = ({
  periods,
  selectedIdx,
  selectedItemRef,
  onSelect,
  className,
}: IcmPeriodListProps) => (
  <Stack
    align="stretch"
    gap={0.5}
    className={cn("overflow-y-auto overscroll-contain p-2", className)}
    role="group"
    aria-label="Pay periods"
  >
    {periods.map((p, i) => {
      const isSelected = i === selectedIdx;
      const status = p.latest_icm_run_status
        ? PAYROLL_RUN_STATUS_UI[p.latest_icm_run_status]
        : null;
      return (
        <Button
          key={p.period_start}
          type="button"
          ref={isSelected ? selectedItemRef : null}
          variant="ghost"
          aria-pressed={isSelected}
          onClick={() => onSelect(p)}
          className="w-full shrink-0 justify-between gap-3 font-normal aria-pressed:bg-selected aria-pressed:font-medium"
        >
          <span>{formatDateRange(p.period_start, p.period_end)}</span>
          {p.is_locked ? (
            <PayPeriodLockedMark />
          ) : p.has_payroll_run && status ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-normal text-subtle">
              <span
                aria-hidden
                className={cn("size-2 rounded-full", status.palette.solid)}
              />
              {status.label}
            </span>
          ) : null}
        </Button>
      );
    })}
  </Stack>
);
